/**
 * Kanonickí vykonávatelia ServiceTask podľa backend invariantu:
 * - executor_ids je kanonická množina, ak je po normalizácii neprázdna,
 * - inak fallback na executor_id,
 * - historické API môže poslať ID ako string alebo number.
 * Konflikt executor_id=106 a executor_ids=[71] => kanonické [71].
 */

export function normalizePositiveUserId(value) {
  if (typeof value === 'string' && value.trim() === '') return null;
  if (typeof value !== 'number' && typeof value !== 'string') return null;
  const n = Number(value);
  if (!Number.isFinite(n) || n <= 0 || !Number.isInteger(n)) return null;
  return n;
}

export function userIdsEqual(a, b) {
  const left = normalizePositiveUserId(a);
  const right = normalizePositiveUserId(b);
  return left != null && right != null && left === right;
}

function uniquePositiveIds(values) {
  const seen = new Set();
  const out = [];
  for (const value of values || []) {
    const id = normalizePositiveUserId(value);
    if (id == null || seen.has(id)) continue;
    seen.add(id);
    out.push(id);
  }
  return out;
}

export function getCanonicalExecutorIds(execution) {
  if (!execution) return [];
  const fromIds = Array.isArray(execution.executor_ids)
    ? uniquePositiveIds(execution.executor_ids)
    : [];
  if (fromIds.length > 0) return fromIds;
  return uniquePositiveIds([execution.executor_id]);
}

/** Jedno kanonické ID pre dropdown; pri 0 alebo >1 vráti null. */
export function getCanonicalExecutorId(execution) {
  const ids = getCanonicalExecutorIds(execution);
  return ids.length === 1 ? ids[0] : null;
}

export function isCanonicalExecutor(execution, userId) {
  const id = normalizePositiveUserId(userId);
  if (id == null) return false;
  return getCanonicalExecutorIds(execution).includes(id);
}

export function getServiceTaskExecutorUiState(execution) {
  const executorIds = getCanonicalExecutorIds(execution);
  return {
    executorIds,
    selectedId: executorIds.length === 1 ? executorIds[0] : null,
    isReadOnly: executorIds.length > 1,
    hasExecutor: executorIds.length > 0,
  };
}

export function pickUsersByCanonicalExecutorIds(users, execution) {
  const byId = new Map();
  for (const user of users || []) {
    const id = normalizePositiveUserId(user?.id);
    if (id == null || byId.has(id)) continue;
    byId.set(id, user);
  }
  return getCanonicalExecutorIds(execution)
    .map((id) => byId.get(id))
    .filter(Boolean);
}

export function findUserByCanonicalExecutorId(users, execution) {
  const selectedId = getCanonicalExecutorId(execution);
  if (selectedId == null) return null;
  return (users || []).find((user) => userIdsEqual(user?.id, selectedId)) || null;
}

/**
 * Manuálny setExecutor payload: iba executor_id, department_id, user_department.
 * Klient neposiela executor_ids — backend z jedného executor_id vytvorí obe polia.
 */
export function buildServiceTaskSetExecutorFields({
  selectType,
  executor,
  userDepartment,
  departmentId,
} = {}) {
  const fields = {
    user_department: userDepartment ?? '',
  };
  if (selectType === 'user') {
    fields.executor_id = executor?.id;
    fields.department_id = '';
  } else if (selectType === 'department') {
    fields.executor_id = '';
    fields.department_id = executor?.id;
  } else {
    fields.department_id = departmentId;
    fields.executor_id = executor?.id;
  }
  return fields;
}
